/**
 * Il contratto con i dispositivi di reparto: oggi la stazione peso/volume.
 *
 * Non è un corpo di richiesta come gli altri — non lo manda una schermata, lo
 * pubblica il gateway sulla coda e lo rilegge chi consuma le misure — ma
 * attraversa lo stesso confine e vale la stessa regola: dichiarato una volta
 * sola, letto da tutti e due i lati.
 */
import { z } from 'zod';
/**
 * Il barcode di un collo, come lo accetta la stazione peso/volume PS130:
 * **solo cifre e trattini, al massimo 35 caratteri** (manuale, tabella 1).
 * È il motivo per cui il `COLLO-001-01` del prototipo non andava bene.
 */
export const zBarcodeCollo = z
    .string()
    .max(35)
    .regex(/^[0-9-]+$/, 'il PS130 accetta solo cifre e trattini nel barcode del collo');
/**
 * La misura che arriva dal PS130, come la ripubblica il gateway sulla coda.
 *
 * ⚠️ **I due flag di unità non sono decorativi, e vanno letti prima di usare i
 * numeri.** Il PS130 può essere configurato in libbre e pollici, e in quel caso
 * li alza: `peso` e le tre misure sono nell'unità dichiarata, non in kg e mm.
 * Zod per difetto **scarta le chiavi che non conosce**, quindi ometterli da
 * questo schema significherebbe farli sparire in silenzio nel `parse` — e una
 * macchina in libbre consegnerebbe numeri trattati come chili, con il peso
 * tassabile sbagliato di un fattore 2,2 senza che nulla protesti.
 *
 * Il consumatore non esiste ancora. Quando esisterà, dovrà convertire **prima**
 * di costruire un `IngombroCollo` di `@errebi/domain`, che vuole kg e cm³.
 */
export const zMisuraCollo = z.object({
    barcode: z.string(),
    lunghezza: z.number().nullable(),
    larghezza: z.number().nullable(),
    altezza: z.number().nullable(),
    unitaMisura: z.string(),
    volumeCm3: z.number().nullable(),
    peso: z.number().nullable(),
    unitaPeso: z.string(),
    /** `true` = i numeri sono in libbre, non in kg. */
    pesoInLibbre: z.boolean(),
    /** `true` = i numeri sono in pollici, non in mm. */
    misureInPollici: z.boolean(),
    lft: z.boolean(),
    progressivo: z.number().nullable(),
    serialNumber: z.string(),
});
/**
 * One weighing as the shop-floor gateway sends it to the cloud: the decoded
 * measure and the frame it came from, so a dispute with the carrier can be
 * rebuilt even if the decoder was wrong that day.
 *
 * `crcValido` lives here and not in `zMisuraCollo`: a frame with a mismatched
 * checksum is accepted, flagged, and the flag has to reach the table.
 */
export const zMisuraPubblicata = z.object({
    misura: zMisuraCollo.extend({ crcValido: z.boolean() }),
    trama: z.string(),
});
/**
 * A number as the ParcelScanServer writes it in its CSV: `0,335`, or a plain
 * JSON number. A comma is not worth a 400 to a supplier who copies its own
 * file field by field.
 */
const zSupplierNumber = z
    .union([
    z.number(),
    z
        .string()
        .trim()
        .regex(/^\d+([.,]\d+)?$/, 'non è un numero'),
])
    .transform((v) => (typeof v === 'number' ? v : Number(v.replace(',', '.'))))
    .pipe(z.number().nonnegative());
/**
 * One weighing as the supplier's ParcelScanServer sends it to the spool over
 * HTTP: the columns of its CSV, same names, in JSON.
 *
 * Measures in **mm**, weight in **kg**: the file says no unit, and that is
 * what the supplier confirmed. `null` = «not measured»; a missing field is a
 * 400, so that a forgotten column does not read as an empty sensor.
 *
 * The barcode is **not** `zBarcodeCollo`: their sample is `06301100000B3`,
 * with a letter. The digits-and-dashes rule belongs to the PS130's own frame.
 * `DataRegistrazione` is kept only in the raw copy: in their sample it is a
 * year ahead.
 */
export const zParcelScanWeighing = z.object({
    ID: zSupplierNumber.pipe(z.number().int()),
    Barcode: z.string().trim().min(1).max(35),
    DataRegistrazione: z.string().optional(),
    Larghezza: zSupplierNumber.nullable(),
    Lunghezza: zSupplierNumber.nullable(),
    Altezza: zSupplierNumber.nullable(),
    VolumeReale: zSupplierNumber.nullable().optional(),
    PesoLordo: zSupplierNumber.nullable(),
});
/** The request body: one row, or the rows of a whole file. Never none. */
export const zParcelScanBody = z.union([
    z.array(zParcelScanWeighing).min(1),
    zParcelScanWeighing.transform((row) => [row]),
]);
/**
 * The body read the way the supplier is told it is read: the rows, or the 400's
 * reason in their words. One function, so that the shop-floor door and the
 * cloud's trial route cannot answer the same body in two ways.
 */
export function readParcelScanBody(value) {
    const parsed = zParcelScanBody.safeParse(value);
    if (parsed.success)
        return { ok: true, rows: parsed.data };
    return {
        ok: false,
        motivo: parsed.error.issues
            .map((i) => `${i.path.join('.') || 'corpo'}: ${i.message}`)
            .join('; '),
    };
}
/**
 * One weighing as the check screen reads it back from `measurements`.
 *
 * Every figure is nullable: `null` is «the scale did not measure it», and
 * turning it into zero would show a weight nobody ever read.
 */
export const zPesata = z.object({
    barcode: z.string(),
    pesoKg: z.number().nullable(),
    lunghezzaMm: z.number().nullable(),
    larghezzaMm: z.number().nullable(),
    altezzaMm: z.number().nullable(),
    volumeCm3: z.number().nullable(),
    /** `false` = NOLFT: the PS130 says the figure is not legal for trade. */
    lft: z.boolean(),
    /** `false` = the frame's checksum did not match; the weighing was kept anyway. */
    crcValido: z.boolean(),
    /** When the cloud stored it, in ISO. */
    misurataIl: z.string(),
});
/** The latest weighings, newest first. */
export const zPesate = z.array(zPesata);
//# sourceMappingURL=devices.js.map